import { useMemo, useRef } from "react"
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Image,
} from "@chakra-ui/react"
import { SupportedTokenName } from "@mezo/sdk"

import dropdownArrow from "#/assets/dropdown-arrow.svg"
import { useTokenSelect } from "#/shared/hooks"
import { PortalToken } from "#/shared/types"

import TokenSelectDropdown from "./TokenSelectDropdown"

type TokenSelectProps = BoxProps & {
  activeToken?: SupportedTokenName
  tokens: PortalToken<SupportedTokenName>[]
  selectTriggerProps?: ButtonProps
  dropdownProps?: BoxProps
  selectCallback?: (token: SupportedTokenName) => void
  withTokenBalance?: boolean
}

export default function TokenSelect({
  activeToken,
  tokens,
  selectTriggerProps,
  dropdownProps,
  selectCallback,
  withTokenBalance,
  ...props
}: TokenSelectProps) {
  const tokenSelectRef = useRef<HTMLDivElement>(null)

  const initialToken = useMemo(
    () => tokens.find(({ token }) => token === activeToken),
    [tokens, activeToken],
  )

  const {
    selectedToken,
    updateSelectedToken,
    isDropdownOpen,
    setIsDropdownOpen,
    dropdownRef,
  } = useTokenSelect(tokenSelectRef, initialToken, selectCallback)

  return (
    <Box
      ref={tokenSelectRef}
      pos="absolute"
      top="1px"
      right="1px"
      h="78px"
      bg="primary.white.100"
      w="120px"
      pt="22px"
      pr="26px"
      rounded="12px"
      {...props}
    >
      <Button
        variant="unstyled"
        alignItems="center"
        justifyContent="space-between"
        display="flex"
        rounded="36px"
        gap={2}
        p="4px 12px 4px 4px"
        bg="primary.black.5"
        pos="relative"
        ml="auto"
        role="group"
        _hover={{ bg: "primary.black.100" }}
        zIndex={10}
        ref={dropdownRef}
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        {...selectTriggerProps}
      >
        <Flex
          w="32px"
          h="32px"
          justifyContent="center"
          alignItems="center"
          pointerEvents="none"
        >
          {selectedToken ? (
            <Image
              w="32px"
              h="32px"
              src={selectedToken.icon}
              _groupHover={{
                filter: `invert(${selectedToken.invertColorOnHover ? 1 : 0})`,
              }}
            />
          ) : (
            <Box w="32px" h="32px" />
          )}
        </Flex>
        <Image
          src={dropdownArrow}
          _groupHover={{ filter: "invert(1)" }}
          pointerEvents="none"
        />
      </Button>
      <TokenSelectDropdown
        selectedToken={selectedToken}
        updateSelectedToken={updateSelectedToken}
        tokens={tokens}
        isDropdownOpen={isDropdownOpen}
        withTokenBalance={withTokenBalance}
        {...dropdownProps}
      />
    </Box>
  )
}
